import { Box, ButtonIcon } from '@comeon/ui';
import { PropsWithChildren, useCallback, useEffect, useRef, useState, Children } from 'react';
import styled from 'styled-components';
import breakpoint from '../../../utils/style-utils';
import Icon, { ICON_LEFT, ICON_RIGHT } from '~/components/icon';
import NavigationDots from '~/components/navigation-dots';

const ScrollContainer = styled.div`
	position: relative;
	overflow-x: scroll;
	scrollbar-width: 0;
	scroll-snap-type: x mandatory;
	scroll-padding: 12px;
	-webkit-overflow-scrolling: touch;
	${breakpoint.lg`
		scrollpadding: 20px;
	`}
	&::-webkit-scrollbar {
		display: none;
	}
`;

const ItemContainer = styled(Box)`
	width: 100%;
	min-width: fit-content;
	padding: 0 12px;
	${breakpoint.lg`
		padding: 0 12px;
	`}
`;

const CardSet = styled.ul`
	list-style-type: none;
	grid-auto-columns: min-content;
	grid-auto-flow: column;
	grid-template-columns: unset;
	display: grid;
	gap: 12px;
	${breakpoint.lg`
		gap: 20px;
	`}
`;

export const ScrollItem = styled.li`
	width: 100%;
	will-change: auto;
	scroll-snap-align: start;
`;

interface Props extends PropsWithChildren {
	chevronEnable?: boolean;
	dotsEnable?: boolean;
	chevronDisplay?: string | string[];
	dotsDisplay?: string | string[];
	chevronLeft?: number | string | number[] | string[];
	chevronRight?: number | string | number[] | string[];
	chevronTop?: number | string;
}

// const MarketplaceHorizontalScroller = styled(Box)`
// 	scroll-snap-type: x mandatory;
// 	width: fit-content;
// 	overflow-x: scroll;
// 	/* scroll-snap-type: both mandatory;
// 	overscroll-behavior-x: contain; */
// 	scroll-behavior: smooth;
// 	display: grid;
// 	grid-auto-flow: column;
// 	grid-gap: 20px;
// 	gap: 20px;
// 	-webkit-overflow-scrolling: touch;
// 	&::-webkit-scrollbar {
// 		display: none;
// 	}
// `;

interface ChevronProps {
	direction: 'left' | 'right';
}

interface ChevronContainerProps {
	left: boolean;
	right: boolean;
	length: number;
	top?: number | string;
	display?: string | string[];
	leftMargin: number | string | number[] | string[];
	rightMargin: number | string | number[] | string[];
	onClick: (direction: 'left' | 'right') => void;
}

const Chevron = styled(Box)<ChevronProps>`
	position: absolute;
	background-color: var(--main-surface-2);
	padding: ${props => (props.direction === 'right' ? '21px 0px 21px 10px' : '21px 10px 21px 0px')};
	border-radius: ${props => (props.direction === 'right' ? '100px 0px 0px 100px' : '0px 100px 100px 0px')};
	z-index: 1;
`;

const ChevronContainer = ({
	left,
	right,
	top,
	length,
	display,
	leftMargin,
	rightMargin,
	onClick
}: ChevronContainerProps) => {
	return (
		<>
			{!left && length !== 1 && (
				<Chevron
					direction="left"
					left={leftMargin}
					marginTop={top}
					// marginTop={CARD_HEIGHT / 2 - 21}
					display={display}
				>
					<ButtonIcon color="var(--main-text-primary)" onClick={() => onClick('left')}>
						<Icon type={ICON_LEFT} />
					</ButtonIcon>
				</Chevron>
			)}
			{!right && length !== 1 && (
				<Chevron
					direction="right"
					right={rightMargin}
					marginTop={top}
					// right={isSpecial(category.name) ? 5 : 0}
					// marginTop={CARD_HEIGHT / 2 - 21}
					display={display}
				>
					<ButtonIcon color="var(--main-text-primary)" onClick={() => onClick('right')}>
						<Icon type={ICON_RIGHT} />
					</ButtonIcon>
				</Chevron>
			)}
		</>
	);
};

const MarketplaceHorizontalScroller = ({
	chevronEnable,
	dotsEnable,
	chevronDisplay,
	dotsDisplay,
	chevronLeft,
	chevronRight,
	chevronTop,
	children
}: Props) => {
	const chevronLeftRef = useRef<HTMLLIElement | null>(null);
	const chevronRightRef = useRef<HTMLLIElement | null>(null);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const itemsRef = useRef<(HTMLLIElement | null)[]>([]);
	const [activeCard, setActiveCard] = useState(-1);
	const [activeDot, setActiveDot] = useState(0);
	const [chevronLeftShow, setChevronLeft] = useState(true);
	const [chevronRightShow, setChevronRight] = useState(true);
	const items = Children.toArray(children);
	// const isChevronLeftInView = useInView(chevronLeftRef);
	// const isChevronRightInView = useInView(chevronRightRef);

	useEffect(() => {
		if (activeCard !== -1) {
			const item = itemsRef.current[activeCard];
			if (item !== null) {
				item.scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'smooth' });
			}
		}
	}, [activeCard]);

	const scrollListener = useCallback(() => {
		if (scrollRef.current !== null) {
			const element = scrollRef.current;
			const wScroll = element.scrollLeft;
			const childWidth = element.scrollWidth / items.length;
			const offset = Math.floor(element.clientWidth / childWidth);
			const totalWidth = element.scrollWidth - element.clientWidth;

			if (items.length > offset) {
				if (Math.floor((wScroll / totalWidth) * (items.length - offset)) === 0) {
					setChevronLeft(true);
				} else {
					setChevronLeft(false);
				}

				if (Math.floor((wScroll / totalWidth) * (items.length - offset)) === items.length - offset) {
					setChevronRight(true);
				} else {
					setChevronRight(false);
				}
			}

			setActiveDot(Math.floor((wScroll / totalWidth) * (items.length - offset)));
		}
	}, [items.length]);

	useEffect(() => {
		scrollListener();
	}, [scrollListener]);

	useEffect(() => {
		const ref = scrollRef.current;
		if (ref !== null) {
			ref.addEventListener('scroll', scrollListener);
		}

		return () => {
			if (ref !== null) {
				ref.removeEventListener('scroll', scrollListener);
			}
		};
	}, [scrollListener, dotsEnable]);

	const handleChevron = useCallback(
		(direction: 'left' | 'right') => {
			if (direction === 'left') {
				const pos = activeDot > 0 ? activeDot - 1 : 0;
				setActiveCard(pos);
			} else if (direction === 'right') {
				const pos = activeDot < items.length - 1 ? activeDot + 1 : items.length - 1;
				setActiveCard(pos);
			}
		},
		[activeDot, items.length]
	);

	return (
		<>
			{chevronEnable && (
				<ChevronContainer
					left={chevronLeftShow}
					right={chevronRightShow}
					leftMargin={chevronLeft || 0}
					rightMargin={chevronRight || 0}
					top={chevronTop}
					length={items.length}
					display={chevronDisplay}
					onClick={handleChevron}
				/>
			)}
			<ScrollContainer ref={scrollRef}>
				<ItemContainer>
					<CardSet>
						{Children.map(children, (child, i) => (
							<ScrollItem
								ref={el => {
									itemsRef.current[i] = el;
									if (items.length !== 1) {
										if (i === 0) {
											chevronLeftRef.current = el;
										}
										if (i === items.length - 1) {
											chevronRightRef.current = el;
										}
									}
								}}
							>
								{child}
							</ScrollItem>
						))}
					</CardSet>
				</ItemContainer>
			</ScrollContainer>
			{dotsEnable && (
				<Box display={dotsDisplay}>
					<NavigationDots length={items.length} activeIndex={activeDot} />
				</Box>
			)}
		</>
	);
};

export default MarketplaceHorizontalScroller;
